import { useState, useEffect } from 'react';
import { useUIStore, useDataStore } from '../stores';
import './DeleteProjectModal.css';

function DeleteProjectModal() {
  const isOpen = useUIStore(state => state.modals.deleteProject);
  const closeModal = useUIStore(state => state.closeModal);
  const deleteProject = useDataStore(state => state.deleteProject);
  const showSuccessNotification = useUIStore(state => state.showSuccessNotification);
  const showErrorNotification = useUIStore(state => state.showErrorNotification);

  const [projectToDelete, setProjectToDelete] = useState(null);
  const [projectNameInput, setProjectNameInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  // Get project from store when modal opens
  useEffect(() => {
    if (isOpen && useUIStore.getState().selectedProjectId) {
      const projects = useDataStore.getState().projects;
      const project = projects.find(p => p.id === useUIStore.getState().selectedProjectId);
      if (project) {
        setProjectToDelete(project);
        setProjectNameInput('');
        setError('');
      }
    }
  }, [isOpen]);

  const handleDelete = async () => {
    if (!projectToDelete) return;

    // Validate project name matches
    if (projectNameInput.trim() !== projectToDelete.project_title.trim()) {
      setError('Project name does not match. Please type the exact project name to confirm deletion.');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      await deleteProject(projectToDelete.id);
      showSuccessNotification(`Project "${projectToDelete.project_title}" deleted successfully`);
      closeModal('deleteProject');
      useUIStore.getState().setSelectedProjectId(null);
      setProjectToDelete(null);
      setProjectNameInput('');
    } catch (error) {
      console.error('[DeleteProjectModal] Error deleting project:', error);
      let errorMessage = 'Failed to delete project';
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      showErrorNotification(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      closeModal('deleteProject');
      useUIStore.getState().setSelectedProjectId(null);
      setProjectToDelete(null);
      setProjectNameInput('');
      setError('');
    }
  };

  if (!isOpen || !projectToDelete) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content delete-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Delete Project</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={isDeleting}
          >
            ×
          </button>
        </div>

        <div className="delete-modal-body">
          <div className="delete-warning">
            <div className="warning-icon">⚠️</div>
            <h4>Are you sure you want to delete this project?</h4>
            <p>This action cannot be undone. All project data will be permanently deleted.</p>
          </div>

          <div className="project-info">
            <div className="info-row">
              <span className="info-label">Project Title:</span>
              <span className="info-value">{projectToDelete.project_title}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Client:</span>
              <span className="info-value">{projectToDelete.client_name}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className="info-value">
                <span className={`status-badge ${projectToDelete.status}`}>
                  {projectToDelete.status.charAt(0).toUpperCase() + projectToDelete.status.slice(1)}
                </span>
              </span>
            </div>
          </div>

          <div className="confirmation-input">
            <label htmlFor="project-name-confirm">
              To confirm deletion, please type the project name: <strong>{projectToDelete.project_title}</strong>
            </label>
            <input
              type="text"
              id="project-name-confirm"
              value={projectNameInput}
              onChange={(e) => {
                setProjectNameInput(e.target.value);
                setError('');
              }}
              placeholder="Type project name to confirm"
              disabled={isDeleting}
              className={error ? 'error' : ''}
              autoFocus
            />
            {error && <span className="error-text">{error}</span>}
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              onClick={handleClose}
              className="btn-secondary"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button 
              type="button" 
              onClick={handleDelete}
              className="btn-danger"
              disabled={isDeleting || projectNameInput.trim() !== projectToDelete.project_title.trim()}
            >
              {isDeleting ? 'Deleting...' : 'Delete Project'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeleteProjectModal;

